const STORAGE_KEYS = {
  verifier: 'ahrefs_pkce_verifier',
  state: 'ahrefs_oauth_state',
} as const;

const ALLOWED_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

function randomString(length: number): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => ALLOWED_CHARS[b % ALLOWED_CHARS.length]).join('');
}

export function generateCodeVerifier(length = 64): string {
  if (length < 43 || length > 128) throw new Error('Code verifier must be 43-128 characters');
  return randomString(length);
}

const STATE_LENGTH = 32;

/**
 * Per-request, cryptographically random anti-CSRF `state`. It is persisted in
 * sessionStorage by `buildAhrefsAuthUrl` and compared against the value Ahrefs
 * echoes back on the redirect. A constant value here would make that check a no-op.
 */
export function generateState(): string {
  return randomString(STATE_LENGTH);
}

async function sha256(input: string): Promise<ArrayBuffer> {
  return crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
}

function base64UrlEncode(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export async function deriveCodeChallenge(codeVerifier: string): Promise<string> {
  const hash = await sha256(codeVerifier);
  return base64UrlEncode(hash);
}

/**
 *
 * https://app.ahrefs.com/web/oauth/authorize?
 * response_type=code&
 * client_id=Respona-staging&
 * scope=apiv3-integration-apps&
 * redirect_uri=https%253A%252F%252Fdev.respona.com%252Fsettings%252Fintegrations%252Fahrefs&
 * code_challenge=uU8ns5IjnFu7kG_083lwIcD--F7k-EHquwAVL63DsvA&
 * code_challenge_method=s256&
 * state=gmejNe9OyPu8gKhRZINmyACuVRmvuw2.
 */
export async function buildAhrefsAuthUrl(clientId: string, redirectUri: string): Promise<string> {
  const codeVerifier = generateCodeVerifier();
  const state = generateState();
  const codeChallenge = await deriveCodeChallenge(codeVerifier);

  sessionStorage.setItem(STORAGE_KEYS.verifier, codeVerifier);
  sessionStorage.setItem(STORAGE_KEYS.state, state);

  const url = new URL('https://app.ahrefs.com/web/oauth/authorize');
  url.search = new URLSearchParams({
    response_type: 'code',
    client_id: encodeURIComponent(clientId),
    scope: 'apiv3-integration-apps',
    redirect_uri: redirectUri,
    code_challenge: codeChallenge,
    code_challenge_method: 's256',
    state,
  }).toString();

  return url.toString();
}

export function readStoredAhrefsPkce(): { codeVerifier: string | null; state: string | null } {
  return {
    codeVerifier: sessionStorage.getItem(STORAGE_KEYS.verifier),
    state: sessionStorage.getItem(STORAGE_KEYS.state),
  };
}
export function clearStoredAhrefsPkce(): void {
  sessionStorage.removeItem(STORAGE_KEYS.verifier);
  sessionStorage.removeItem(STORAGE_KEYS.state);
}
